// Каталог упражнений для студента: список типов упражнений с фильтрацией по уровню сложности.

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Spinner,
  Alert,
} from 'react-bootstrap';
import AppNavbar from '../../components/common/AppNavbar';
import { getStudentExerciseTypes, getStudentExercises } from '../../api/studentApi';
import { getComplexityLevels } from '../../api/exerciseApi';

const LEVEL_VARIANTS = ['success', 'info', 'primary', 'warning', 'danger'];

/** Returns a Bootstrap badge variant cycling through LEVEL_VARIANTS by level id. */
function levelVariant(levelId) {
  const idx = (levelId - 1) % LEVEL_VARIANTS.length;
  return LEVEL_VARIANTS[idx < 0 ? 0 : idx];
}

export default function StudentExercisesPage() {
  const navigate = useNavigate();

  // ── Типы упражнений ─────────────────────────────────────────────────────────
  const [types, setTypes]             = useState([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [typesError, setTypesError]   = useState('');

  // ── Раскрытый тип и его упражнения ─────────────────────────────────────────
  const [expandedTypeId, setExpandedTypeId] = useState(null);
  const [exercises, setExercises]           = useState({});
  const [exLoading, setExLoading]           = useState({});
  const [exError, setExError]               = useState({});

  // ── Уровни сложности ───────────────────────────────────────────────────────
  const [complexityLevels, setComplexityLevels] = useState([]);

  // ── Фильтр уровня ──────────────────────────────────────────────────────────
  const [activeLevel, setActiveLevel] = useState(null);

  useEffect(() => {
    setTypesLoading(true);
    getStudentExerciseTypes()
      .then(({ data }) => setTypes(data))
      .catch(() => setTypesError('Не удалось загрузить типы упражнений'))
      .finally(() => setTypesLoading(false));
    getComplexityLevels()
      .then(({ data }) => setComplexityLevels(data))
      .catch(() => {});
  }, []);

  const loadExercises = (typeId, levelId) => {
    setExLoading((prev) => ({ ...prev, [typeId]: true }));
    setExError((prev) => ({ ...prev, [typeId]: '' }));
    getStudentExercises(typeId, levelId)
      .then(({ data }) => setExercises((prev) => ({ ...prev, [typeId]: data })))
      .catch(() => setExError((prev) => ({ ...prev, [typeId]: 'Не удалось загрузить упражнения' })))
      .finally(() => setExLoading((prev) => ({ ...prev, [typeId]: false })));
  };

  const handleToggleType = (type) => {
    if (expandedTypeId === type.id) {
      setExpandedTypeId(null);
      return;
    }
    setExpandedTypeId(type.id);
    loadExercises(type.id, activeLevel);
  };

  const handleLevelFilter = (levelId) => {
    const newLevel = activeLevel === levelId ? null : levelId;
    setActiveLevel(newLevel);
    if (expandedTypeId) {
      loadExercises(expandedTypeId, newLevel);
    }
  };

  return (
    <>
      <AppNavbar />
      <div style={{ background: 'var(--ewl-bg-body)', minHeight: 'calc(100vh - 56px)' }}>
        <Container className="py-4">

          {/* ── Заголовок ── */}
          <div className="mb-4 pb-3" style={{ borderBottom: '1px solid #e9ecef' }}>
            <h5 className="fw-bold mb-0" style={{ letterSpacing: '-0.01em', color: '#1e2a4a' }}>
              Упражнения
            </h5>
            <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.875rem' }}>
              Выполняйте упражнения и отслеживайте свой прогресс
            </p>
          </div>

          {/* ── Фильтр уровня ── */}
          <div className="d-flex flex-wrap gap-2 mb-4">
            <Button
              size="sm"
              variant={activeLevel === null ? 'secondary' : 'outline-secondary'}
              onClick={() => handleLevelFilter(null)}
              style={{ borderRadius: '2rem', fontWeight: 500, fontSize: '0.8125rem' }}
            >
              Все
            </Button>
            {complexityLevels.map((level) => (
              <Button
                key={level.id}
                size="sm"
                variant={activeLevel === level.id ? levelVariant(level.id) : `outline-${levelVariant(level.id)}`}
                onClick={() => handleLevelFilter(level.id)}
                style={{ borderRadius: '2rem', fontWeight: 500, fontSize: '0.8125rem' }}
              >
                {level.name}
              </Button>
            ))}
          </div>

          {/* ── Список типов (аккордеон) ── */}
          {typesLoading && (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          )}

          {typesError && (
            <Alert variant="danger" style={{ borderRadius: '0.75rem' }}>{typesError}</Alert>
          )}

          {!typesLoading && !typesError && types.length === 0 && (
            <Card className="border-0 text-center py-5"
              style={{ background: '#fff', borderRadius: '0.875rem', boxShadow: '0 1px 6px rgba(0,0,0,0.07)' }}>
              <Card.Body>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📚</div>
                <h6 className="fw-semibold text-muted mb-1">Типы упражнений пока не добавлены</h6>
              </Card.Body>
            </Card>
          )}

          {!typesLoading && !typesError && (
            <div className="d-flex flex-column gap-3">
              {types.map((type) => {
                const isExpanded    = expandedTypeId === type.id;
                const typeExercises = exercises[type.id] ?? [];
                const isLoadingEx   = exLoading[type.id];
                const errorEx       = exError[type.id];

                return (
                  <Card key={type.id} className="border-0"
                    style={{ background: '#fff', borderRadius: '0.875rem',
                      boxShadow: '0 1px 6px rgba(0,0,0,0.07)', overflow: 'hidden' }}>

                    {/* Заголовок типа */}
                    <div
                      className="p-4 d-flex align-items-start justify-content-between"
                      onClick={() => handleToggleType(type)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          {type.hard_level && (() => {
                            const lvl = complexityLevels.find((l) => l.id === type.hard_level);
                            return (
                              <Badge bg={levelVariant(type.hard_level)}
                                style={{ fontSize: '0.72rem', fontWeight: 600, flexShrink: 0 }}>
                                {lvl ? lvl.name : type.hard_level}
                              </Badge>
                            );
                          })()}
                          <h6 className="fw-bold mb-0"
                            style={{ color: '#1e2a4a', fontSize: '1rem', lineHeight: 1.35 }}>
                            {type.name}
                          </h6>
                        </div>
                        {type.description && (
                          <p className="text-muted mb-0"
                            style={{ fontSize: '0.8375rem', lineHeight: 1.55 }}>
                            {type.description}
                          </p>
                        )}
                      </div>

                      <div className="ms-3 flex-shrink-0 d-flex align-items-center justify-content-center"
                        style={{
                          width: '1.75rem', height: '1.75rem', borderRadius: '50%',
                          background: isExpanded ? 'var(--bs-primary)' : '#f4f6fb',
                          color: isExpanded ? '#fff' : '#6c757d',
                          fontSize: '0.75rem', fontWeight: 700,
                          transition: 'all 0.2s ease',
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}>
                        ▼
                      </div>
                    </div>

                    {/* Список упражнений */}
                    {isExpanded && (
                      <div style={{ borderTop: '1px solid #e9ecef', background: '#f8f9fc', padding: '1rem' }}>
                        {isLoadingEx && (
                          <div className="d-flex justify-content-center py-3">
                            <Spinner animation="border" variant="primary" size="sm" />
                          </div>
                        )}
                        {errorEx && (
                          <Alert variant="danger" className="mb-0 py-2"
                            style={{ borderRadius: '0.5rem', fontSize: '0.875rem' }}>
                            {errorEx}
                          </Alert>
                        )}
                        {!isLoadingEx && !errorEx && typeExercises.length === 0 && (
                          <p className="text-muted mb-0 text-center py-2" style={{ fontSize: '0.875rem' }}>
                            Упражнений пока нет — преподаватель ещё не добавил
                          </p>
                        )}
                        {!isLoadingEx && !errorEx && typeExercises.length > 0 && (
                          <Row className="g-2">
                            {typeExercises.map((ex) => {
                              const variantCount  = ex.variant_count ?? null;
                              const historyStatus = ex.history_status ?? ex.last_attempt_status ?? null;

                              return (
                                <Col xs={12} md={6} key={ex.id}>
                                  <Card className="border-0 ewl-exercise-card"
                                    onClick={() => navigate('/student/exercises/exercise', { state: { exerciseId: ex.id } })}
                                    style={{ background: '#fff', borderRadius: '0.75rem',
                                      boxShadow: '0 1px 4px rgba(0,0,0,0.06)', cursor: 'pointer' }}>
                                    <Card.Body className="p-3">
                                      <div className="d-flex align-items-start justify-content-between gap-2 mb-1">
                                        <h6 className="fw-semibold mb-0"
                                          style={{ color: '#1e2a4a', fontSize: '0.9rem', lineHeight: 1.4 }}>
                                          {ex.name}
                                        </h6>
                                        <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                          {ex.complexity_level ? (
                                            <Badge bg={levelVariant(ex.complexity_level.id)}
                                              style={{ fontSize: '0.65rem', fontWeight: 600 }}>
                                              {ex.complexity_level.name}
                                            </Badge>
                                          ) : ex.complexity_level_id ? (
                                            <Badge bg={levelVariant(ex.complexity_level_id)}
                                              style={{ fontSize: '0.65rem', fontWeight: 600 }}>
                                              {complexityLevels.find((l) => l.id === ex.complexity_level_id)?.name ?? ex.complexity_level_id}
                                            </Badge>
                                          ) : null}
                                          {historyStatus != null && (
                                            <Badge
                                              bg={historyStatus === 1 ? 'success' : 'warning'}
                                              text={historyStatus === 0 ? 'dark' : undefined}
                                              style={{ fontSize: '0.65rem', fontWeight: 500 }}>
                                              {historyStatus === 1 ? 'Завершено' : 'Не завершено'}
                                            </Badge>
                                          )}
                                        </div>
                                      </div>

                                      {ex.task && (
                                        <div style={{ background: '#f4f6fb', borderRadius: '0.4rem',
                                          padding: '0.4rem 0.6rem', fontSize: '0.78rem',
                                          color: '#495057', lineHeight: 1.5, marginTop: '0.5rem' }}>
                                          <span className="fw-semibold text-muted"
                                            style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                            Задание:
                                          </span>{' '}
                                          {ex.task.length > 100 ? ex.task.slice(0, 100) + '...' : ex.task}
                                        </div>
                                      )}

                                      {variantCount != null && (
                                        <div className="mt-2 d-flex align-items-center gap-1"
                                          style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                                          <span style={{ display: 'inline-block', width: '1rem', height: '1rem',
                                            background: '#e9ecef', borderRadius: '0.2rem',
                                            textAlign: 'center', lineHeight: '1rem',
                                            fontSize: '0.65rem', fontWeight: 700 }}>
                                            {variantCount}
                                          </span>
                                          {variantCount === 1 ? 'вариант' : variantCount < 5 ? 'варианта' : 'вариантов'}
                                        </div>
                                      )}
                                    </Card.Body>
                                  </Card>
                                </Col>
                              );
                            })}
                          </Row>
                        )}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}

        </Container>
      </div>
    </>
  );
}
